import { type Virtualizer } from '@tanstack/react-virtual';
import { type KeyboardEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useIsomorphicLayoutEffect } from 'react-use';

import { type FlatListItem } from '~/components/v2/components/shared/selectionBar/types';

export const getAgentSelectionListItemId = (listIndex: number) => `agent-selection-list-item-${listIndex}`;

type ScrollAlign = 'auto' | 'end' | 'start';

type NavigationDirection = 'down' | 'end' | 'home' | 'pointer' | 'up';

const getScrollAlignForNavigation = (
  prevNavigableIndex: number,
  nextNavigableIndex: number,
  navigableCount: number,
  direction: NavigationDirection,
): ScrollAlign => {
  if (navigableCount <= 1) {
    return 'auto';
  }

  if (direction === 'home') {
    return 'start';
  }

  if (direction === 'end') {
    return 'end';
  }

  if (direction === 'pointer') {
    return 'auto';
  }

  if (direction === 'down' && nextNavigableIndex === 0 && prevNavigableIndex === navigableCount - 1) {
    return 'start';
  }

  if (direction === 'up' && nextNavigableIndex === navigableCount - 1 && prevNavigableIndex === 0) {
    return 'end';
  }

  // Let the virtualizer scroll only when the target row is out of view. Forcing start/end here
  // re-scrolls rows that are already visible and makes absolute-positioned items appear to jump.
  return 'auto';
};

const getNextNavigableIndexForDirection = (
  prev: number,
  navigableCount: number,
  direction: 'down' | 'end' | 'home' | 'up',
): number => {
  if (direction === 'home') {
    return 0;
  }

  if (direction === 'end') {
    return navigableCount - 1;
  }

  if (direction === 'down') {
    return (prev + 1) % navigableCount;
  }

  return (prev - 1 + navigableCount) % navigableCount;
};

const getNextNavigableIndexForArrowKey = (prev: number, navigableCount: number, direction: 'down' | 'up'): number => {
  if (direction === 'down') {
    if (prev < 0) {
      return 0;
    }

    return (prev + 1) % navigableCount;
  }

  if (prev < 0) {
    return navigableCount - 1;
  }

  return (prev - 1 + navigableCount) % navigableCount;
};

type UseAgentSelectionListKeyboardNavParams = {
  allowUnselect: boolean;
  isListOpen: boolean;
  itemList: FlatListItem[];
  scrollElement: HTMLElement | null;
  virtualizer: Virtualizer<HTMLElement, Element>;
};

export const useAgentSelectionListKeyboardNav = ({
  allowUnselect,
  isListOpen,
  itemList,
  scrollElement,
  virtualizer,
}: UseAgentSelectionListKeyboardNavParams) => {
  const navigableIndices = useMemo(
    () =>
      itemList
        .map((item, index) => (item.type !== 'header' && allowUnselect ? index : null))
        .filter((idx): idx is number => idx !== null),
    [allowUnselect, itemList],
  );

  const navigableIndicesKey = useMemo(() => navigableIndices.join(','), [navigableIndices]);

  const [selectedNavigableIndex, setSelectedNavigableIndex] = useState(-1);
  const [shouldFocusUnselectButton, setShouldFocusUnselectButton] = useState(false);
  const scrollAlignRef = useRef<ScrollAlign>('auto');

  const updateNavigableSelection = useCallback(
    (nextNavigableIndex: number, prevNavigableIndex: number, direction: NavigationDirection) => {
      scrollAlignRef.current = getScrollAlignForNavigation(
        prevNavigableIndex,
        nextNavigableIndex,
        navigableIndices.length,
        direction,
      );

      if (direction === 'pointer' && scrollElement) {
        const activeElement = document.activeElement;

        if (
          activeElement instanceof HTMLElement &&
          scrollElement.contains(activeElement) &&
          activeElement !== scrollElement
        ) {
          activeElement.blur();
          scrollElement.focus({ preventScroll: true });
        }
      }

      setShouldFocusUnselectButton(direction !== 'pointer');
      setSelectedNavigableIndex(nextNavigableIndex);
    },
    [navigableIndices.length, scrollElement],
  );

  const selectedListIndex = selectedNavigableIndex >= 0 ? (navigableIndices[selectedNavigableIndex] ?? null) : null;

  const activeDescendantId = selectedListIndex !== null ? getAgentSelectionListItemId(selectedListIndex) : undefined;

  // biome-ignore lint/correctness/useExhaustiveDependencies: reset highlight when navigable rows change
  useEffect(() => {
    setSelectedNavigableIndex(-1);
    setShouldFocusUnselectButton(false);
  }, [navigableIndicesKey]);

  useEffect(() => {
    if (!isListOpen) {
      setSelectedNavigableIndex(-1);
      setShouldFocusUnselectButton(false);
    }
  }, [isListOpen]);

  useEffect(() => {
    if (!scrollElement) {
      return;
    }

    if (activeDescendantId) {
      scrollElement.setAttribute('aria-activedescendant', activeDescendantId);
    } else {
      scrollElement.removeAttribute('aria-activedescendant');
    }
  }, [activeDescendantId, scrollElement]);

  useIsomorphicLayoutEffect(() => {
    if (selectedListIndex === null) {
      return;
    }

    virtualizer.scrollToIndex(selectedListIndex, { align: scrollAlignRef.current });
  }, [selectedListIndex, virtualizer]);

  const moveSelection = useCallback(
    (direction: 'down' | 'end' | 'home' | 'up') => {
      if (navigableIndices.length === 0) {
        return;
      }

      setSelectedNavigableIndex((prev) => {
        const next = getNextNavigableIndexForDirection(prev, navigableIndices.length, direction);

        scrollAlignRef.current = getScrollAlignForNavigation(prev, next, navigableIndices.length, direction);
        return next;
      });
      setShouldFocusUnselectButton(true);
    },
    [navigableIndices.length],
  );

  const handleListKeyDown = useCallback(
    (event: KeyboardEvent | globalThis.KeyboardEvent) => {
      if (navigableIndices.length === 0) {
        return;
      }

      const handleArrowKey = (direction: 'down' | 'up') => {
        event.preventDefault();
        event.stopPropagation();

        setSelectedNavigableIndex((prev) => {
          const next = getNextNavigableIndexForArrowKey(prev, navigableIndices.length, direction);

          scrollAlignRef.current = getScrollAlignForNavigation(prev, next, navigableIndices.length, direction);
          return next;
        });
        setShouldFocusUnselectButton(true);
      };

      if (event.key === 'ArrowDown') {
        handleArrowKey('down');
        return;
      }

      if (event.key === 'ArrowUp') {
        handleArrowKey('up');
        return;
      }

      if (event.key === 'Home') {
        event.preventDefault();
        event.stopPropagation();
        moveSelection('home');
        return;
      }

      if (event.key === 'End') {
        event.preventDefault();
        event.stopPropagation();
        moveSelection('end');
      }
    },
    [moveSelection, navigableIndices.length],
  );

  const handleItemMouseEnter = useCallback(
    (listIndex: number) => {
      const navigableIndex = navigableIndices.indexOf(listIndex);

      if (navigableIndex >= 0) {
        updateNavigableSelection(navigableIndex, selectedNavigableIndex, 'pointer');
      }
    },
    [navigableIndices, selectedNavigableIndex, updateNavigableSelection],
  );

  return {
    activeDescendantId,
    handleItemMouseEnter,
    handleListKeyDown,
    selectedListIndex,
    shouldFocusUnselectButton,
  };
};
